import { useCallback, useMemo, useRef, useState } from "react";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeTemplate
} from "@/api/types/outgoingIntake";
import type { LibrarySource } from "@/features/intakeBuilder";
import { isUserFacingTemplate } from "@/features/requestsIde/model";

/** The catalog templates whose questions make up the question bank. */
const BANK_TEMPLATES = [
  "biographical",
  "address_history",
  "employment_history",
  "immigration_history",
  "dependents",
  "documents"
];

const questionsOf = (
  nodes: OutgoingIntakeFormNode[]
): OutgoingIntakeFormNode[] =>
  nodes.flatMap((n) =>
    n.node_type === "question" ? [n] : questionsOf(n.children ?? [])
  );

/** "From Library" for the template editor: the question bank and the forms. */
export const useTemplateLibrary = (
  catalog: OutgoingIntakeTemplate[]
): LibrarySource => {
  const [bank, setBank] = useState<OutgoingIntakeTemplate[]>([]);
  const requested = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (requested.current) return;
    requested.current = true;
    void OutgoingIntakesAPI.listTemplates({ templateIds: BANK_TEMPLATES })
      .then((loaded) =>
        setBank(loaded.filter((t) => BANK_TEMPLATES.includes(t.template_id)))
      )
      .catch(() => {
        requested.current = false;
      });
  }, []);

  return useMemo(
    () => ({
      bank: BANK_TEMPLATES.map((id) => bank.find((t) => t.template_id === id))
        .filter((t): t is OutgoingIntakeTemplate => Boolean(t))
        .map((t) => ({
          id: t.template_id,
          label: t.label,
          questions: questionsOf(t.schema ?? [])
        })),
      forms: catalog.filter(isUserFacingTemplate).map((t) => ({
        id: t.template_id,
        label: t.label,
        meta: `${t.question_count ?? 0} questions`
      })),
      ensureLoaded
    }),
    [bank, catalog, ensureLoaded]
  );
};
